/**
 * SpecSheetPanel — renders each line item as a TECHNICAL DATASHEET, allows
 * editing quote quantities inline, exposes the write-capable chat editor, and
 * a PDF export.
 */
import React, { useState, useEffect } from 'react';
import { Download, Send, Sparkles, ChevronDown, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { forgeAPI } from '../../../services/api';
import { useForge } from '../ForgeContext';
import ImageLightbox from '../ImageLightbox';
import PdfModal from '../PdfModal';

const Section = ({ title, items }) =>
  items && items.length ? (
    <div className="forge-ds-section">
      <h4>{title}</h4>
      <ul>{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  ) : null;

const SpecSheetPanel = ({ focusLineItem = null, showEditor = true, hideToolbar = false }) => {
  const { tenderId, summary, refresh } = useForge();
  const sheet = summary?.spec_sheet;
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [editQty, setEditQty] = useState({});
  // null = default (only the first datasheet open); otherwise an explicit Set.
  const [openItems, setOpenItems] = useState(null);
  const [images, setImages] = useState([]);
  const [lightbox, setLightbox] = useState(null);   // image index or null
  const [pdf, setPdf] = useState(null);             // { url, title } or null

  useEffect(() => {
    if (!tenderId) return undefined;
    let on = true;
    forgeAPI.getSpecImages(tenderId)
      .then((d) => { if (on) setImages(d.images || []); })
      .catch(() => {});
    return () => { on = false; };
  }, [tenderId]);

  // Cockpit navigator focus: open + scroll to the chosen datasheet.
  useEffect(() => {
    if (focusLineItem == null) return;
    setOpenItems(new Set([focusLineItem]));
    const t = setTimeout(() => {
      document.getElementById(`forge-ds-${focusLineItem}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 60);
    return () => clearTimeout(t);
  }, [focusLineItem]);

  if (!sheet) {
    return (
      <div className="forge-empty">
        <p>No spec sheet yet.</p>
        <button
          className="forge-btn-primary"
          onClick={async () => { setBusy(true); await forgeAPI.generateSpecSheet(tenderId); await refresh(); setBusy(false); }}
          disabled={busy}
        >
          {busy ? 'Generating…' : 'Generate spec sheet'}
        </button>
      </div>
    );
  }

  const saveQty = async (lineItem) => {
    const raw = editQty[lineItem];
    if (raw === undefined) return;
    const qtys = raw.split(',').map((s) => parseFloat(s.trim())).filter((n) => !isNaN(n));
    await forgeAPI.setQuoteQuantity(tenderId, lineItem, qtys);
    setEditQty((p) => { const n = { ...p }; delete n[lineItem]; return n; });
    await refresh();
  };

  const sendChat = async () => {
    if (!msg.trim()) return;
    const userMsg = msg.trim();
    setChat((c) => [...c, { role: 'user', content: userMsg }]);
    setMsg('');
    setBusy(true);
    try {
      const res = await forgeAPI.chatSpecSheet(tenderId, userMsg, chat);
      setChat((c) => [...c, { role: 'assistant', content: res.reply }]);
      await refresh();
    } catch (e) {
      setChat((c) => [...c, { role: 'assistant', content: 'Error: ' + (e.message || 'failed') }]);
    } finally {
      setBusy(false);
    }
  };

  const lineNos = sheet.line_items.map((li) => li.line_item);
  const effectiveOpen = openItems ?? new Set(lineNos.slice(0, 1)); // first open by default
  const isOpen = (n) => effectiveOpen.has(n);
  const toggle = (n) => {
    const s = new Set(effectiveOpen);
    s.has(n) ? s.delete(n) : s.add(n);
    setOpenItems(s);
  };
  const allOpen = lineNos.length > 0 && lineNos.every((n) => effectiveOpen.has(n));
  const toggleAll = () => setOpenItems(allOpen ? new Set() : new Set(lineNos));

  return (
    <div className="forge-spec">
      {!hideToolbar && (
      <div className="forge-spec-toolbar">
        <div className="forge-muted">
          {sheet.line_items.length} datasheet{sheet.line_items.length === 1 ? '' : 's'} ·
          coverage {sheet.coverage?.extracted ?? '?'}/{sheet.coverage?.expected ?? '?'}
          {sheet.sample_required && <span className="forge-chip">sample required</span>}
        </div>
        <div className="forge-ds-head-actions">
          <button className="forge-btn-ghost" onClick={toggleAll}>
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
          <a className="forge-btn-ghost" href={forgeAPI.specSheetExportUrl(tenderId)} target="_blank" rel="noreferrer">
            <Download size={14} /> Export PDF
          </a>
        </div>
      </div>
      )}

      {images.length > 0 && (
        <div className="forge-ds-section" style={{ marginBottom: 14 }}>
          <h4><ImageIcon size={13} /> Images &amp; reference files ({images.length})</h4>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {images.map((im, i) => (
              <button key={i} type="button" className="forge-ds-thumb"
                onClick={() => setLightbox(i)}
                title={[im.source, im.page ? `p${im.page}` : null].filter(Boolean).join(' · ')}>
                <img src={im.data_url || im.url} alt={im.source || 'image'} />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={`forge-spec-grid ${showEditor ? '' : 'no-editor'}`}>
        <div className="forge-spec-sheets">
          {sheet.line_items.map((li) => (
            <div key={li.line_item} id={`forge-ds-${li.line_item}`} className="forge-ds">
              <div
                className="forge-ds-head forge-ds-head-toggle"
                onClick={() => toggle(li.line_item)}
              >
                <h3>
                  {isOpen(li.line_item) ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  <span className="forge-ds-num">#{li.line_item}</span>
                  {li.product_name}
                </h3>
                <div className="forge-ds-head-actions" onClick={(e) => e.stopPropagation()}>
                  {li.sku && <span className="forge-chip">{li.sku}</span>}
                  <button
                    className="forge-btn-sm"
                    onClick={() => setPdf({ url: forgeAPI.specSheetExportUrl(tenderId, li.line_item),
                      title: `#${li.line_item} ${li.product_name}` })}
                    title="View this datasheet as PDF"
                  >
                    <Download size={13} /> PDF
                  </button>
                </div>
              </div>
              {isOpen(li.line_item) && (
              <div className="forge-ds-body">
              {li.purpose && <p className="forge-ds-purpose">{li.purpose}</p>}

              {li.oem_reference && Object.values(li.oem_reference).some(Boolean) && (
                <div className="forge-ds-section">
                  <h4>OEM Reference</h4>
                  <div className="forge-kv">
                    {Object.entries(li.oem_reference).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k}><span>{k}</span><b>{v}</b></div>
                    ))}
                  </div>
                </div>
              )}

              <Section title="Equivalent product(s)" items={li.equivalent_products} />

              <Section title="Key Functional Requirements" items={li.key_functional_requirements} />

              {li.technical_specifications?.length > 0 && (
                <div className="forge-ds-section">
                  <h4>Technical Specifications</h4>
                  <table className="forge-ds-table">
                    <thead><tr><th>Specification</th><th>Requirement</th></tr></thead>
                    <tbody>
                      {li.technical_specifications.map((t, i) => (
                        <tr key={i}><td>{t.specification}</td><td>{t.requirement}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <Section title="Required Certifications" items={li.required_certifications} />
              <Section title="Required Supplier Documentation" items={li.required_supplier_documentation} />
              <Section title="Additional Requirements" items={li.additional_requirements} />

              <div className="forge-ds-qty">
                <label>Quantity to quote suppliers</label>
                <input
                  value={editQty[li.line_item] ?? (li.quote_quantities || []).join(', ')}
                  onChange={(e) => setEditQty((p) => ({ ...p, [li.line_item]: e.target.value }))}
                />
                {editQty[li.line_item] !== undefined && (
                  <button className="forge-btn-sm" onClick={() => saveQty(li.line_item)}>Save</button>
                )}
                <small className="forge-muted forge-qty-help">
                  Commercial RFQ ask — the quantity the agent asks factories to price
                  (not printed on the supplier datasheet).{' '}
                  {li.quantity_rationale}
                </small>
              </div>
              </div>
              )}
            </div>
          ))}
        </div>

        {showEditor && (
        <div className="forge-spec-chat">
          <div className="forge-chat-head"><Sparkles size={14} /> Edit via chat</div>
          <div className="forge-chat-log">
            {chat.length === 0 && <p className="forge-muted">e.g. “set line 1 sample to not required” or “add ISO 9001 to line 2 certs”.</p>}
            {chat.map((m, i) => (
              <div key={i} className={`forge-chat-msg forge-chat-${m.role}`}>{m.content}</div>
            ))}
          </div>
          <div className="forge-chat-input">
            <input
              value={msg}
              placeholder="Ask FORGE to edit the spec…"
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendChat()}
              disabled={busy}
            />
            <button className="forge-btn-sm" onClick={sendChat} disabled={busy}><Send size={14} /></button>
          </div>
        </div>
        )}
      </div>

      {lightbox != null && (
        <ImageLightbox images={images} index={lightbox} onIndex={setLightbox}
          onClose={() => setLightbox(null)} />
      )}
      {pdf && <PdfModal url={pdf.url} title={pdf.title} onClose={() => setPdf(null)} />}
    </div>
  );
};

export default SpecSheetPanel;
